(() => {
  const root = document.querySelector('[data-case-index]');
  if (!root) return;

  const isNational = root.hasAttribute('data-national-index');
  const cards = Array.from(root.querySelectorAll('[data-index-card]'));
  const states = Array.from(root.querySelectorAll('[data-index-state]'));
  const search = root.querySelector('[data-index-search]');
  const type = root.querySelector('[data-index-type]');
  const state = root.querySelector('[data-index-state-select]');
  const count = root.querySelector('[data-index-count]');
  const empty = root.querySelector('[data-index-empty]');
  const more = root.querySelector('[data-index-more]');
  const reset = root.querySelector('[data-index-reset]');
  const resultGrid = root.querySelector('[data-index-search-results]');
  const fixedState = root.dataset.fixedState || '';
  const fixedType = root.dataset.fixedType || '';
  const pageSize = 50;
  let displayLimit = pageSize;
  let remoteRecords = [];
  let remoteLoadPromise = null;
  let latestMatches = [];

  const normalize = (value) => String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const slug = (value) => normalize(value).replace(/\s+/g, '-');

  const updateURL = () => {
    const params = new URLSearchParams();
    if (search.value.trim()) params.set('q', search.value.trim());
    if (!fixedType && type.value) params.set('type', type.value);
    if (!fixedState && state.value) params.set('state', state.value);
    const suffix = params.toString();
    history.replaceState(null, '', `${location.pathname}${suffix ? `?${suffix}` : ''}`);
  };

  const hydrate = () => {
    const params = new URLSearchParams(location.search);
    search.value = params.get('q') || '';
    type.value = fixedType || params.get('type') || '';
    state.value = fixedState || params.get('state') || '';
  };

  const loadRemoteRecords = () => {
    if (remoteLoadPromise) return remoteLoadPromise;
    const url = root.dataset.searchDataUrl;
    remoteLoadPromise = fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Catalog request failed: ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        remoteRecords = Array.isArray(payload.records) ? payload.records : [];
        return remoteRecords;
      });
    return remoteLoadPromise;
  };

  const makeLink = (label, href, className = '') => {
    const link = document.createElement('a');
    link.textContent = label;
    link.href = href;
    if (className) link.className = className;
    if (/^https?:/i.test(href)) link.rel = 'noopener';
    return link;
  };

  const renderRemoteCard = (record) => {
    const typeLabel = record.t === 'm' ? 'Missing Person' : 'Serial Killer Profile';
    const recordURL = record.t === 'm'
      ? `/pages/missing-persons-${slug(record.s)}#record-${slug(record.i)}-${slug(record.s)}`
      : record.j;
    const deepLink = record.t === 'm'
      ? `almostsolved://missing-person/${encodeURIComponent(record.i)}`
      : `almostsolved://serial-killer/${slug(record.n)}`;
    const appStoreURL = `https://apps.apple.com/us/app/almost-solved-true-crime/id6758279097?pt=homepage&ct=case_${encodeURIComponent(slug(record.i))}&mt=8`;
    const article = document.createElement('article');
    article.className = 'as-case-index__card';
    const meta = document.createElement('p');
    meta.className = 'as-case-index__meta';
    meta.textContent = [typeLabel, record.y].filter(Boolean).join(' · ');
    const heading = document.createElement('h3');
    heading.textContent = record.n;
    const place = document.createElement('p');
    place.className = 'as-case-index__place';
    place.textContent = `${record.l} · ${record.s}`;
    const actions = document.createElement('div');
    actions.className = 'as-case-index__actions';
    if (recordURL) actions.append(makeLink('Open case record', recordURL, 'as-case-index__record-link'));
    actions.append(makeLink('Open in Almost Solved', deepLink, 'as-case-index__case-link'));
    actions.append(makeLink('Download the app', appStoreURL, 'as-case-index__download-link'));
    article.append(meta, heading, place, actions);
    return article;
  };

  const renderRemoteMatches = () => {
    resultGrid.replaceChildren(...latestMatches.slice(0, displayLimit).map(renderRemoteCard));
    const uniqueMatches = new Set(latestMatches.map((record) => record.i));
    count.textContent = `Found ${uniqueMatches.size.toLocaleString()} ${uniqueMatches.size === 1 ? 'indexed record' : 'indexed records'}`;
    empty.hidden = latestMatches.length !== 0;
    if (!latestMatches.length) empty.textContent = 'No indexed record matches those terms. Try part of a name, a city, county, or State.';
    more.hidden = latestMatches.length <= displayLimit;
  };

  const applyNational = async ({ resetLimit = true } = {}) => {
    if (resetLimit) displayLimit = pageSize;
    updateURL();
    const hasFilter = search.value.trim() || type.value || state.value;
    if (!hasFilter) {
      latestMatches = [];
      resultGrid.replaceChildren();
      count.textContent = 'Enter a name or place, or choose a State or index.';
      empty.hidden = false;
      empty.textContent = 'Enter a name or place, or choose a State or index.';
      more.hidden = true;
      return;
    }
    count.textContent = 'Searching the index…';
    empty.hidden = true;
    try {
      await loadRemoteRecords();
      const queryTokens = normalize(search.value).split(' ').filter(Boolean);
      latestMatches = remoteRecords.filter((record) => {
        const typeValue = record.t === 'm' ? 'missing-person' : 'serial-killer-profile';
        const searchable = normalize(`${record.n} ${record.l} ${record.s} ${typeValue}`);
        return queryTokens.every((token) => searchable.includes(token))
          && (!type.value || typeValue === type.value)
          && (!state.value || slug(record.s) === state.value);
      });
      renderRemoteMatches();
    } catch (_error) {
      latestMatches = [];
      resultGrid.replaceChildren();
      count.textContent = 'The index could not be loaded.';
      empty.hidden = false;
      empty.textContent = 'Use a State directory below while the national search is unavailable.';
      more.hidden = true;
    }
  };

  const applyState = ({ resetLimit = true } = {}) => {
    if (resetLimit) displayLimit = pageSize;
    const queryTokens = normalize(search.value).split(' ').filter(Boolean);
    const matching = cards.filter((card) => {
      const searchable = normalize(card.dataset.search || '');
      return queryTokens.every((token) => searchable.includes(token))
        && (!type.value || card.dataset.type === type.value)
        && (!state.value || card.dataset.state === state.value);
    });
    const uniqueMatches = new Set(matching.map((card) => card.dataset.recordId));
    cards.forEach((card) => { card.hidden = true; });
    matching.slice(0, displayLimit).forEach((card) => { card.hidden = false; });
    states.forEach((section) => {
      const visibleCount = matching.filter((card) => section.contains(card)).length;
      section.hidden = !section.querySelector('[data-index-card]:not([hidden])');
      const stateCount = section.querySelector('.as-case-index__state-heading p');
      if (stateCount) stateCount.textContent = `${visibleCount.toLocaleString()} ${visibleCount === 1 ? 'record' : 'records'}`;
    });
    count.textContent = `Found ${uniqueMatches.size.toLocaleString()} ${uniqueMatches.size === 1 ? 'indexed record' : 'indexed records'}`;
    empty.hidden = matching.length !== 0;
    more.hidden = matching.length <= displayLimit;
    latestMatches = matching;
    updateURL();
  };

  const apply = (options) => isNational ? applyNational(options) : Promise.resolve(applyState(options));
  let inputTimer;
  search.addEventListener('input', () => {
    clearTimeout(inputTimer);
    inputTimer = setTimeout(() => apply(), 120);
  });
  type.addEventListener('change', () => apply());
  state.addEventListener('change', () => apply());
  more.addEventListener('click', () => {
    displayLimit += pageSize;
    if (isNational) renderRemoteMatches();
    else apply({ resetLimit: false });
  });
  reset.addEventListener('click', () => {
    search.value = '';
    type.value = fixedType;
    state.value = fixedState;
    apply();
    search.focus();
  });
  hydrate();
  root.classList.add('as-case-index--enhanced');
  apply();
})();
